import { ButtonTypes } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { useRecoilValue } from 'recoil';

import { useSuitabilityCheck } from '../../../../../hooks/useSuitabilityCheck';
import { customerItemState } from '../../../../../store/customer/atom';
import { useOrderActions } from '../../../../../store/order/actions';
import { orderItemState } from '../../../../../store/order/atom';
import { hasOrderItem } from '../../../../../store/order/selectors';
import { useProductActions } from '../../../../../store/product/actions';
import { AssetSelectedTypeMap, SelectedProduct } from '../../../../../store/product/types';
import { ProductButtonProps } from '../../../../BatchTicket/components/DynamicButton';
import { ResponsiveButton } from '../ResponsiveButton';

export interface ApplicationButtonProps<T extends keyof AssetSelectedTypeMap>
  extends ProductButtonProps<T> {
  tooltip?: string;
  idSelector: keyof AssetSelectedTypeMap[T];
  buttonText?: string;
  buttonColor?: ColorTypes.Option;
  buttonIcon?: IconographyTypes.Option;
  mobileButtonIconColor?: ColorTypes.Option;
  buttonVariant?: ButtonTypes.Variant;
  backgroundColor?: ColorTypes.Option;
}

export const ApplicationButton = <T extends keyof AssetSelectedTypeMap>({
  id,
  offer,
  tooltip,
  isDisabled,
  buttonText = 'Investir',
  buttonColor = 'neutral.white',
  mobileButtonIconColor = 'feedback.success.default',
  buttonIcon = 'Check',
  buttonVariant,
  backgroundColor,
  productContextConstant,
}: ApplicationButtonProps<T>) => {
  const hasOrder = useRecoilValue(hasOrderItem);
  const orderItem = useRecoilValue(orderItemState);
  const customerItem = useRecoilValue(customerItemState);

  const { ModalComponent, checkSuitability, isCheckingSuitability } = useSuitabilityCheck(
    customerItem.current.accountNumber
  );
  const { handleSelectOrderItem, updateOrderStatus } = useOrderActions();
  const { handleSelectProductItem, updateProductStatus } = useProductActions();

  const getTypeFromId = () => {
    const mapper = {
      fixedIncome: 'FixedIncome',
      fixedIncomeSell: 'FixedIncome',
      directTreasury: 'DirectTreasury',
      directTreasurySell: 'DirectTreasury',
      equity: 'Equity',
      equitySell: 'Equity',
      coes: 'StructuredOperations',
      funds: 'Fund',
      fundsSell: 'Fund',
    };
    return mapper[id as keyof typeof mapper]!;
  };

  const handleSelectProduct = async () => {
    handleSelectProductItem({
      ...offer,
      id,
      type: getTypeFromId(),
      operationType: productContextConstant.operationType,
    } as SelectedProduct);

    updateProductStatus({
      label: 'Produto selecionado',
      status: 'completed',
    });

    updateOrderStatus('opened');

    if (hasOrder) {
      handleSelectOrderItem({ name: orderItem.name, description: orderItem.description });
      updateOrderStatus('completed');
    }
  };

  const handlePress = () => {
    checkSuitability(id, offer, handleSelectProduct);
  };

  return (
    <>
      {ModalComponent}
      <ResponsiveButton
        text={buttonText}
        color={buttonColor}
        id={id}
        offer={offer}
        icon={buttonIcon}
        mobileIconColor={buttonColor}
        mobileButtonIconColor={mobileButtonIconColor}
        isDisabled={isDisabled}
        isLoading={isCheckingSuitability}
        onPress={handlePress}
        buttonVariant={buttonVariant}
        backgroundColor={backgroundColor}
        tooltip={tooltip}
        productContextConstant={productContextConstant}
      />
    </>
  );
};
